import { useState, useEffect, useCallback } from "react";
import {
  collection, addDoc, deleteDoc, updateDoc,
  doc, query, orderBy, onSnapshot, serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";

export function usePendientes() {
  const { user, profile } = useAuth();
  const [pendientes, setPendientes] = useState([]);
  const [loading, setLoading] = useState(true);

  const workspaceId = profile?.activeWorkspaceId || profile?.personalWorkspaceId || null;
  const pendientesRef = useCallback(() => {
    if (workspaceId) return collection(db, "workspaces", workspaceId, "pendientes");
    return collection(db, "users", user.uid, "pendientes");
  }, [workspaceId, user]);
  const pendienteDoc = useCallback((id) => {
    if (workspaceId) return doc(db, "workspaces", workspaceId, "pendientes", id);
    return doc(db, "users", user.uid, "pendientes", id);
  }, [workspaceId, user]);

  useEffect(() => {
    if (!user) return;
    const ref = pendientesRef();
    const q = query(ref, orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, snap => {
      setPendientes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [user, workspaceId]);

  async function addPendiente(data) {
    if (!user) return;
    await addDoc(pendientesRef(), {
      ...data,
      pagado: false,
      pagos: [],
      addedBy: user.uid,
      createdAt: serverTimestamp(),
    });
  }

  async function deletePendiente(id) {
    await deleteDoc(pendienteDoc(id));
  }

  async function updatePendiente(id, data) {
    await updateDoc(pendienteDoc(id), data);
  }

  function fmtDatetime(d) {
    return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
  }

  const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
                  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

  // Suma ya abonada de un pendiente (compatibilidad con pendientes antiguos sin "pagos")
  function getPagado(pendiente) {
    return (pendiente.pagos || []).reduce((s, p) => s + p.amount, 0);
  }

  function getRestante(pendiente) {
    return Math.max(0, (pendiente.amount || 0) - getPagado(pendiente));
  }

  // Registra un abono parcial (o total) sobre un pendiente, con una nota
  // de texto libre opcional (ej. "Julia", "mitad de la cena", etc.) para
  // recordar el detalle de ese abono concreto.
  // Si el abono cubre el resto del importe, el pendiente se completa:
  // se crea la entrada en el historial y se borra el documento de pendientes.
  // Si es parcial, se añade al array "pagos" y el pendiente sigue abierto
  // por el importe restante.
  async function registrarPago(pendiente, montoAbono, addEntry, nota, fechaPago) {
    // Si el usuario eligió una fecha, usarla; si no, usar la fecha actual
    const now = fechaPago ? (() => {
      const [y, m, d] = fechaPago.split("-");
      return new Date(y, m-1, d, new Date().getHours(), new Date().getMinutes());
    })() : new Date();
    const dt = fmtDatetime(now);
    const restanteAntes = getRestante(pendiente);
    const abono = Math.min(montoAbono, restanteAntes);
    if (abono <= 0) return;
    const notaLimpia = (nota || "").trim();
    const restanteDespues = restanteAntes - abono;
    const esPagoFinal = restanteDespues <= 0.0001;

    // El abono siempre se refleja como movimiento real en el historial/Panel.
    // Si el pendiente tiene campos personalizados (modo negocio: Cliente,
    // Matrícula, Referencia), se trasladan también a esa entrada.
    const customData = {};
    ["custom1", "custom2", "custom3"].forEach(key => {
      if (pendiente[key]) customData[key] = pendiente[key];
    });
    // Si el pendiente es una "operación combinada" (ingreso + gasto juntos,
    // ej. cobro con comisión descontada), ese desglose solo tiene sentido
    // trasladarlo tal cual cuando se cobra/paga de una sola vez por el
    // importe completo — en un abono parcial no hay forma de repartir el
    // desglose proporcionalmente, así que ese caso se guarda como una
    // entrada normal (con el tipo/importe netos, sin desglose).
    const combinedData = (pendiente.isCombined && esPagoFinal)
      ? { isCombined: true, incomeAmount: pendiente.incomeAmount || 0, expenseAmount: pendiente.expenseAmount || 0, expenseDesc: pendiente.expenseDesc || "" }
      : {};
    await addEntry({
      type: pendiente.type,
      cat: pendiente.cat || "General",
      desc: notaLimpia ? `${pendiente.desc} (${notaLimpia})` : pendiente.desc,
      payment: pendiente.payment || "",
      amount: abono,
      month: MONTHS[now.getMonth()],
      year: now.getFullYear(),
      datetime: dt,
      // Se traspasan también al historial, si el pendiente los tenía —
      // antes se perdían al cobrar/pagar, aunque el pendiente sí los
      // guardara (clienteId ya se perdía desde antes de tocar nada del
      // ticket; receiptPath es nuevo y tenía el mismo problema).
      ...(pendiente.clienteId ? { clienteId: pendiente.clienteId } : {}),
      ...(pendiente.receiptPath ? { receiptPath: pendiente.receiptPath } : {}),
      ...customData,
      ...combinedData,
    });

    if (esPagoFinal) {
      // Completado: se elimina el pendiente, ya quedó todo reflejado en entries
      await deleteDoc(pendienteDoc(pendiente.id));
    } else {
      // Parcial: se guarda el abono (con su nota) en el historial del propio pendiente
      const nuevosPagos = [...(pendiente.pagos || []), { amount: abono, date: dt, nota: notaLimpia }];
      await updateDoc(pendienteDoc(pendiente.id), {
        pagos: nuevosPagos,
      });
    }
  }

  // Mantenido por compatibilidad: marca como pagado el 100% restante de una vez
  async function marcarPagado(pendiente, addEntry) {
    const restante = getRestante(pendiente);
    await registrarPago(pendiente, restante, addEntry);
  }

  return {
    pendientes, loading,
    addPendiente, deletePendiente, updatePendiente,
    marcarPagado, registrarPago,
    getPagado, getRestante,
  };
}
